// GA4 loader and safe tracking API (Task 20).
// The Measurement ID is public by design (visible in the shipped JS), so it
// lives in REACT_APP_GA4_MEASUREMENT_ID — never a private key there.
// When the variable is absent/invalid everything here is a no-op.

const measurementId = (process.env.REACT_APP_GA4_MEASUREMENT_ID || "").trim();

let initialized = false;
let lastPageKey = null;

export function isGa4Enabled() {
  return /^G-[A-Z0-9]+$/i.test(measurementId);
}

function gtag(...args) {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag(...args);
  }
}

export function initGa4() {
  if (!isGa4Enabled() || initialized || typeof document === "undefined") {
    return;
  }

  initialized = true;
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function () {
    window.dataLayer.push(arguments);
  };

  // Advanced Consent Mode: denied by default (no Analytics cookies are set).
  // Kept before any config/event command. A CMP can later call
  // updateAnalyticsConsent() after a real user choice.
  gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    wait_for_update: 500,
  });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  script.onload = () => {
    gtag("js", new Date());
    // Manual page views because this is a client-routed SPA.
    gtag("config", measurementId, {
      send_page_view: false,
      anonymize_ip: true,
    });
  };
  script.onerror = () => {
    // Analytics failure must never break the application.
    initialized = false;
  };
  document.head.appendChild(script);
}

export function trackPageView({ pathname, search = "", title } = {}) {
  if (!isGa4Enabled()) return;

  const pageLocation = `${window.location.origin}${pathname}${search}`;
  const pageKey = `${pageLocation}|${title || document.title}`;
  if (pageKey === lastPageKey) return; // guards StrictMode/dev repeats
  lastPageKey = pageKey;

  gtag("event", "page_view", {
    page_title: title || document.title,
    page_location: pageLocation,
    language: document.documentElement.lang || undefined,
  });
}

export function trackEvent(name, params = {}) {
  if (!isGa4Enabled() || !name) return;
  gtag("event", name, params);
}

// Only call after a user's actual choice in a consent solution.
export function updateAnalyticsConsent(granted) {
  if (!isGa4Enabled()) return;
  gtag("consent", "update", {
    analytics_storage: granted ? "granted" : "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
}
